import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import GlobalChatWidget from './components/common/GlobalChatWidget';
import Footer from './components/Footer';
import Home from './components/Home';
import Services from './components/Services';
import Pricing from './components/Pricing';
import Contact from './components/Contact';
import About from './components/About';
import Choice from './components/Choice';
import StudentForm from './components/StudentForm';
import TestForm from './components/TestFrom';
import WizardPage from './pages/WizardPage';
import ChatDeepseekPage from './pages/ChatDeepseekPage';
import ChatN8nPage from './pages/ChatN8nPage';
import ConsultationBookingPage from './pages/ConsultationBookingPage';
import BlogPage from './pages/BlogPage';
import ManagePosts from './pages/ManagePosts';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminProfilePage from './pages/AdminProfilePage';
import BlogSEOCommandCenter from './pages/admin/system/BlogSEOCommandCenter';
import BlogCommandCenter from './pages/admin/system/BlogCommandCenter';
import SubmissionDetail from './pages/admin/leads/SubmissionDetail';
import CaseBuilderPage from './pages/admin/cases/CaseBuilderPage';
import CaseDetailPage from './pages/admin/cases/CaseDetailPage';
import CasesListPage from './pages/admin/cases/CasesListPage';
import AgreementsBuilderPage from './pages/admin/agreements/AgreementsBuilderPage';
import AgreementsListPage from './pages/admin/agreements/AgreementsListPage';
import AgreementDetailPage from './pages/admin/agreements/AgreementDetailPage';
import AgreementTemplatesPage from './pages/admin/agreements/AgreementTemplatesPage';
import StaffPage from './pages/admin/system/StaffPage';
import ClientsListPage from './pages/admin/clients/ClientsListPage';
import AddClientPage from './pages/admin/clients/AddClientPage';
import ClientDetailPage from './pages/admin/clients/ClientDetailPage';
import ProgramMatchingPage from './pages/admin/education/ProgramMatchingPage';
import VisaApplicationDetailPage from './pages/admin/applications/VisaApplicationDetailPage';
import TaskDetailPage from './pages/admin/cases/TaskDetailPage';
import TaskCreatePage from './pages/admin/cases/TaskCreatePage';
import UniversityApplicationCreatePage from './pages/admin/applications/UniversityApplicationCreatePage';
import VisaApplicationCreatePage from './pages/admin/applications/VisaApplicationCreatePage';
import OfferCreatePage from './pages/admin/agreements/OfferCreatePage';
import OfferDetailPage from './pages/admin/agreements/OfferDetailPage';
import AdminLayout from './components/admin/AdminLayout';
import AdminControlRoom from './pages/admin/AdminControlRoom';
import AnalyticsDashboard from './pages/admin/analytics/AnalyticsDashboard';
import CountryManager from './pages/admin/content/CountryManager';
import PathwayManager from './pages/admin/content/PathwayManager';
import PackageManager from './pages/admin/content/PackageManager';
import TranslationManager from './pages/admin/content/TranslationManager';
import WizardExitManager from './pages/admin/leads/WizardExitManager';
import WizardSubmissionsManager from './pages/admin/leads/WizardSubmissionsManager';
import LeadsManager from './pages/admin/leads/LeadsManager';
import DocumentsManager from './pages/admin/cases/DocumentsManager';
import AuditLogViewer from './pages/admin/system/AuditLogViewer';
import StaffWiki from './pages/admin/system/StaffWiki';
import UniversitiesCatalogPage from './pages/admin/education/UniversitiesCatalogPage';
import UniversityDetailPage from './pages/admin/education/UniversityDetailPage';
import ProgramDetailPage from './pages/admin/education/ProgramDetailPage';
import UniversityApplicationsPage from './pages/admin/applications/UniversityApplicationsPage';
import UniversityApplicationDetailPage from './pages/admin/applications/UniversityApplicationDetailPage';
import VisaProductsPage from './pages/admin/system/VisaProductsPage';
import VisaApplicationsPage from './pages/admin/applications/VisaApplicationsPage';
import AIModelManagement from './components/admin/AIModels/AIModelManagement';
import MultilingualTestPage from './components/testing/MultilingualTestPage';
import AgreementAcceptPage from './pages/AgreementAcceptPage';
import AgreementSignedPage from './pages/AgreementSignedPage';
import ClientPortalLayout from './pages/client/ClientPortalLayout';
import ClientDashboardPage from './pages/client/ClientDashboardPage';
import ClientApplicationsPage from './pages/client/ClientApplicationsPage';
import ClientDocumentsPage from './pages/client/ClientDocumentsPage';
import ClientAgreementsPage from './pages/client/ClientAgreementsPage';
import ClientPaymentsPage from './pages/client/ClientPaymentsPage';
import ClientMessagesPage from './pages/client/ClientMessagesPage';
import ClientProfilePage from './pages/client/ClientProfilePage';
import ClientCredentialsPage from './pages/client/ClientCredentialsPage';
import ClientApplicationDetailPage from './pages/client/ClientApplicationDetailPage';
import ClientCaseDetailPage from './pages/client/ClientCaseDetailPage';
import PendingTasksPage from './pages/admin/cases/PendingTasksPage';
import UrgentDeadlinesPage from './pages/admin/cases/UrgentDeadlinesPage';
import ProgramCasesPage from './pages/admin/education/ProgramCasesPage';

// ... existing imports ...

import ScrollToTop from './components/common/ScrollToTop';
import SmsTestPage from './pages/SmsTestPage';

const STAFF_ROLES = new Set(['admin', 'manager', 'agreements', 'docs', 'visa', 'cro', 'education']);

const RequireStaff = ({ children }) => {
  const { role, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading workspace…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  // Check metadata role in addition to the derived role to prevent race conditions
  const metadataRole =
    user?.app_metadata?.role ||
    user?.user_metadata?.role;

  if (!STAFF_ROLES.has(role) && !(metadataRole && STAFF_ROLES.has(metadataRole))) {
    return <Navigate to="/portal" replace />;
  }

  return children;
};

function AppShell() {
  const location = useLocation();
  const path = location.pathname || '';
  const isFullScreenChat = path.startsWith('/chat-n8n') || path.startsWith('/chat');
  const isChatRoute = path.startsWith('/chat-n8n') || path.startsWith('/chat');
  const isAdminRoute = path.startsWith('/admin');

  return (
    <div className="font-sans antialiased text-gray-800 bg-white leading-relaxed min-h-screen flex flex-col">
      <ScrollToTop />
      {!isFullScreenChat && !isAdminRoute && <Header />}
      <main className="flex-grow">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/choice" element={<Choice />} />
          <Route path="/book-consultation" element={<ConsultationBookingPage />} />
          <Route path="/studentform" element={<StudentForm />} />
          <Route path="/wizard" element={<WizardPage />} />
          <Route path="/chat" element={<ChatDeepseekPage />} />
          <Route path="/chat-n8n" element={<ChatN8nPage />} />
          {/* Redirect /whygermany to the blog post */}
          <Route path="/whygermany" element={<Navigate to="/blog/1" replace />} />
          <Route path="/testform" element={<TestForm />} />
          <Route path="/agreements/:id/accept" element={<AgreementAcceptPage />} />
          <Route path="/agreements/:id/signed" element={<AgreementSignedPage />} />

          {/* Blog Routes */}
          <Route path="/blog/*" element={<BlogPage />} />

          {/* Client Portal */}
          <Route path="/portal" element={<ClientPortalLayout />}>
            <Route index element={<ClientDashboardPage />} />
            <Route path="applications" element={<ClientApplicationsPage />} />
            <Route path="applications/:id" element={<ClientApplicationDetailPage />} />
            <Route path="cases/:id" element={<ClientCaseDetailPage />} />
            <Route path="documents" element={<ClientDocumentsPage />} />
            <Route path="agreements" element={<ClientAgreementsPage />} />
            <Route path="payments" element={<ClientPaymentsPage />} />
            <Route path="messages" element={<ClientMessagesPage />} />
            <Route path="credentials" element={<ClientCredentialsPage />} />
            <Route path="profile" element={<ClientProfilePage />} />
          </Route>

          {/* Admin Routes (temporarily ungated while auth loop is resolved) */}
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />

          <Route path="/admin" element={<RequireStaff><AdminLayout><Outlet /></AdminLayout></RequireStaff>}>
            <Route index element={<AdminControlRoom />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="leads" element={<LeadsManager />} />
            <Route path="submissions" element={<WizardSubmissionsManager />} />
            <Route path="submissions/:id" element={<SubmissionDetail />} />
            <Route path="wizard" element={<WizardExitManager />} />
            <Route path="countries" element={<CountryManager />} />
            <Route path="pathways" element={<PathwayManager />} />
            <Route path="packages" element={<PackageManager />} />
            <Route path="translations" element={<TranslationManager />} />

            <Route path="cases" element={<CasesListPage />} />
            <Route path="cases/new" element={<CaseBuilderPage />} />
            <Route path="cases/:id" element={<CaseDetailPage />} />

            <Route path="documents" element={<DocumentsManager />} />

            <Route path="clients" element={<ClientsListPage />} />
            <Route path="clients/new" element={<AddClientPage />} />
            <Route path="clients/:id" element={<ClientDetailPage />} />

            <Route path="visa-products" element={<VisaProductsPage />} />
            <Route path="visa-apps" element={<VisaApplicationsPage />} />
            <Route path="visa_apps/new" element={<VisaApplicationCreatePage />} />
            <Route path="visa_apps/:id" element={<VisaApplicationDetailPage />} />

            <Route path="agreements" element={<AgreementsListPage />} />
            <Route path="agreements/new" element={<AgreementsBuilderPage />} />
            <Route path="agreements/templates" element={<AgreementTemplatesPage />} />
            <Route path="agreements/:id" element={<AgreementDetailPage />} />

            <Route path="universities" element={<UniversitiesCatalogPage />} />
            <Route path="universities/:id" element={<UniversityDetailPage />} />
            <Route path="programs/:id" element={<ProgramDetailPage />} />
            <Route path="uni-apps" element={<UniversityApplicationsPage />} />
            <Route path="univ_apps/new" element={<UniversityApplicationCreatePage />} />
            <Route path="univ_apps/:id" element={<UniversityApplicationDetailPage />} />
            <Route path="programs/:programId/cases" element={<ProgramCasesPage />} />

            <Route path="matching" element={<ProgramMatchingPage />} />
            <Route path="staff" element={<StaffPage />} />
            <Route path="blog" element={<BlogSEOCommandCenter />} />
            <Route path="blog/legacy" element={<BlogCommandCenter />} />
            <Route path="wiki" element={<StaffWiki />} />
            <Route path="audit" element={<AuditLogViewer />} />
            <Route path="ai-models" element={<AIModelManagement />} />
            <Route path="profile" element={<AdminProfilePage />} />
            <Route path="tasks" element={<PendingTasksPage />} />
            <Route path="tasks/new" element={<TaskCreatePage />} />
            <Route path="tasks/:id" element={<TaskDetailPage />} />
            <Route path="tasks/pending" element={<PendingTasksPage />} />
            <Route path="deadlines/urgent" element={<UrgentDeadlinesPage />} />
            <Route path="offers/new" element={<OfferCreatePage />} />
            <Route path="offers/:id" element={<OfferDetailPage />} />
            <Route path="sms-test" element={<SmsTestPage />} />
          </Route>

          <Route path="/manage" element={<RequireStaff><ManagePosts /></RequireStaff>} />

          {/* Testing Routes */}
          <Route path="/test-i18n" element={<MultilingualTestPage />} />
          <Route path="/admin/sms-test" element={<RequireStaff><SmsTestPage /></RequireStaff>} />
        </Routes>
      </main>
      {!isFullScreenChat && <Footer />}
      {/* Global Chat Widget - hidden on dedicated chat and admin routes */}
      {!isChatRoute && !isAdminRoute && <GlobalChatWidget />}
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <AppShell />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
